import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { ReactComponent as WalkerIcon } from '../assets/walking-person.svg';
import { ReactComponent as TrophyIcon } from '../assets/trophy-basic.svg';
import { ReactComponent as FlagIcon } from '../assets/flag.svg';
import mainCompletionSound from '../assets/main-timer-completion.mp3';
import auxiliaryCompletionSound from '../assets/auxiliary-timer-completion-warning.mp3';
import timerFailureSound from '../assets/timer-failure.mp3';
import startTimerSound from '../assets/start-timer.mp3';
import { AUTO_START_MAIN_KEY, DEFAULT_TASK_CATEGORY, TASK_CATEGORY_OPTIONS } from '../constants';
import { collection, addDoc, onSnapshot, query, orderBy, deleteDoc, doc, getDocs, setDoc, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import CircularTimer from './CircularTimer';

const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m
    .toString()
    .padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const breakdownDuration = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { hours, minutes, seconds };
};

const describeDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const parts = [];
  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  }
  if (secs > 0) {
    parts.push(`${secs} second${secs === 1 ? '' : 's'}`);
  }
  if (parts.length === 0) {
    return 'a moment';
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return parts.join(' and ');
};

const TimerPage = ({
  durationSeconds = 30 * 60,
  title = 'Main Chain',
  successPrefix = 'Session',
  storageKey,
  intentStorageKey,
  durationPreferenceKey,
  isAuxiliary = false,
}) => {
  const initialBreakdown = breakdownDuration(durationSeconds);
  const [baseDuration, setBaseDuration] = useState(durationSeconds);
  const [timeLeft, setTimeLeft] = useState(durationSeconds);
  const [isActive, setIsActive] = useState(false);
  const [targetTimestamp, setTargetTimestamp] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalSession, setModalSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionStart, setSessionStart] = useState(null);
  const [intentDetails, setIntentDetails] = useState(null);
  const [customHours, setCustomHours] = useState(initialBreakdown.hours.toString());
  const [customMinutes, setCustomMinutes] = useState(initialBreakdown.minutes.toString());
  const [customSeconds, setCustomSeconds] = useState(initialBreakdown.seconds.toString());
  const [durationError, setDurationError] = useState('');
  const [isIntentModalOpen, setIsIntentModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_TASK_CATEGORY);
  const [customCategory, setCustomCategory] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [showFocusFailPrompt, setShowFocusFailPrompt] = useState(false);
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [confirmContinue, setConfirmContinue] = useState(false);
  const [durationLoaded, setDurationLoaded] = useState(false);
  const auxiliaryWindowLabel = useMemo(() => describeDuration(baseDuration), [baseDuration]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.uid;
  const mainSoundRef = useRef(null);
  const auxSoundRef = useRef(null);
  const startSoundRef = useRef(null);
  const lastTickRef = useRef(null);

  const userDurationPreferenceKey = useMemo(() => {
    if (!durationPreferenceKey) {
      return null;
    }
    if (!uid) {
      return durationPreferenceKey;
    }
    return `${durationPreferenceKey}-${uid}`;
  }, [durationPreferenceKey, uid]);

  const timerDocRef = useMemo(() => {
    if (!uid) {
      return null;
    }
    return doc(db, 'users', uid, 'activeTimers', isAuxiliary ? 'aux' : 'main');
  }, [uid, isAuxiliary]);

  const clearActiveTimerDoc = useCallback(() => {
    if (!timerDocRef) {
      return;
    }
    deleteDoc(timerDocRef).catch(() => { });
  }, [timerDocRef]);

  const persistActiveTimer = useCallback(
    (startDate, targetDate, intentSnapshot) => {
      if (!timerDocRef) {
        return;
      }
      setDoc(
        timerDocRef,
        {
          startTimestamp: startDate,
          targetTimestamp: targetDate,
          baseDuration,
          intentDetails: intentSnapshot ?? null,
          updatedAt: new Date(),
        },
        { merge: true }
      ).catch(() => { });
    },
    [timerDocRef, baseDuration]
  );


  useEffect(() => {
    mainSoundRef.current = new Audio(mainCompletionSound);
    auxSoundRef.current = new Audio(auxiliaryCompletionSound);
    startSoundRef.current = new Audio(startTimerSound);
    failureSoundRef.current = new Audio(timerFailureSound);
    return () => {
      if (mainSoundRef.current) {
        mainSoundRef.current.pause();
        mainSoundRef.current = null;
      }
      if (auxSoundRef.current) {
        auxSoundRef.current.pause();
        auxSoundRef.current = null;
      }
      if (startSoundRef.current) {
        startSoundRef.current.pause();
        startSoundRef.current = null;
      }
      if (failureSoundRef.current) {
        failureSoundRef.current.pause();
        failureSoundRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setDurationLoaded(false);
    let initialDuration = durationSeconds;
    if (typeof window !== 'undefined' && userDurationPreferenceKey) {
      const storedDuration = Number(localStorage.getItem(userDurationPreferenceKey));
      if (!Number.isNaN(storedDuration) && storedDuration > 0) {
        initialDuration = storedDuration;
      }
    }
    setBaseDuration(initialDuration);
    setTimeLeft(initialDuration);
    setIsActive(false);
    setShowModal(false);
    setModalSession(null);
    setSessionStart(null);
    setTargetTimestamp(null);
    setConfirmContinue(false);
    const { hours, minutes, seconds } = breakdownDuration(initialDuration);
    setCustomHours(hours.toString());
    setCustomMinutes(minutes.toString());
    setCustomSeconds(seconds.toString());
    setDurationError('');
    setDurationLoaded(true);
  }, [durationSeconds, userDurationPreferenceKey]);

  useEffect(() => {
    if (!intentStorageKey || typeof window === 'undefined') {
      setIntentDetails(null);
      return;
    }
    try {
      const storedIntent = localStorage.getItem(intentStorageKey);
      if (!storedIntent) {
        setIntentDetails(null);
        return;
      }
      const parsedIntent = JSON.parse(storedIntent);
      if (parsedIntent && typeof parsedIntent === 'object') {
        setIntentDetails(parsedIntent);
      } else {
        setIntentDetails(null);
      }
    } catch (error) {
      console.warn('Failed to parse sacred seat intent', error);
      setIntentDetails(null);
    }
  }, [intentStorageKey]);

  useEffect(() => {
    if (!uid) {
      setSessions([]);
      return undefined;
    }
    const colName = isAuxiliary ? 'auxSessions' : 'mainSessions';
    const q = query(collection(db, 'users', uid, colName), orderBy('startTimestamp', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const total = snapshot.docs.length;
        const mapped = snapshot.docs.map((docSnap, index) => {
          const data = docSnap.data();
          const startStamp = data.startTimestamp;
          const endStamp = data.endTimestamp;
          const startDate = startStamp?.toDate ? startStamp.toDate() : new Date(startStamp || Date.now());
          const endDate = endStamp?.toDate ? endStamp.toDate() : new Date(endStamp || Date.now());
          return {
            id: docSnap.id,
            number: total - index,
            chainNumber: total - index,
            date: startDate.toLocaleDateString([], { month: 'short', day: 'numeric' }),
            startTime: startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            endTime: endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            activityType: data.activityType,
            activityDescription: data.activityDescription,
          };
        });
        setSessions(mapped);
      },
      (error) => {
        console.warn('Failed to subscribe to sessions', error);
        setSessions([]);
      }
    );
    return () => unsubscribe();
  }, [uid, isAuxiliary]);

  const isFinalizingRef = useRef(false);
  const failureSoundRef = useRef(null);
  const lastFinalizedStartRef = useRef(null);

  const clearSessions = useCallback(async () => {
    if (!uid) {
      setSessions([]);
      return;
    }
    const colName = isAuxiliary ? 'auxSessions' : 'mainSessions';
    try {
      const colRef = collection(db, 'users', uid, colName);
      const snap = await getDocs(colRef);
      await Promise.all(snap.docs.map((docSnap) => deleteDoc(doc(db, 'users', uid, colName, docSnap.id))));
    } catch (error) {
      console.warn('Failed to clear sessions', error);
    }
  }, [uid, isAuxiliary]);

  const saveToGraveyard = useCallback(async () => {
    if (!uid || isAuxiliary || sessions.length === 0) {
      return;
    }
    try {
      await addDoc(collection(db, 'users', uid, 'graveyard'), {
        chainLength: sessions.length,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.warn('Failed to save to graveyard', error);
    }
  }, [uid, isAuxiliary, sessions]);

  const handleAuxiliaryFailure = useCallback(async () => {
    failureSoundRef.current?.play().catch(() => { });
    await saveToGraveyard();
    await clearSessions();
    setIsActive(false);
    setTimeLeft(baseDuration);
    setSessionStart(null);
    setTargetTimestamp(null);
    setShowModal(false);
    setShowFocusFailPrompt(false);
    clearActiveTimerDoc();
  }, [baseDuration, clearSessions, clearActiveTimerDoc, saveToGraveyard]);

  const finalizeSession = useCallback(
    async ({ endDate = new Date(), silent = false, startOverride = null } = {}) => {
      if (!uid || isFinalizingRef.current) {
        return;
      }

      const startDate = startOverride
        ? new Date(startOverride)
        : sessionStart
          ? new Date(sessionStart)
          : new Date(endDate.getTime() - baseDuration * 1000);

      const startTime = startDate.getTime();
      if (
        lastFinalizedStartRef.current &&
        Math.abs(lastFinalizedStartRef.current - startTime) < 2000
      ) {
        return;
      }

      isFinalizingRef.current = true;
      lastFinalizedStartRef.current = startTime;

      const activityType = intentDetails?.categoryLabel || 'Focus Session';
      const activityDescription = intentDetails?.description || '';
      const colName = isAuxiliary ? 'auxSessions' : 'mainSessions';

      try {
        // Check for existing session with same start time (prevent cross-tab duplicates)
        const duplicateQuery = query(
          collection(db, 'users', uid, colName),
          where('startTimestamp', '==', startDate)
        );
        const duplicateSnap = await getDocs(duplicateQuery);
        if (!duplicateSnap.empty) {
          console.log('Duplicate session detected in Firestore, skipping.');
          clearActiveTimerDoc();
          return;
        }

        const sessionData = {
          startTimestamp: startDate,
          endTimestamp: endDate,
          ...(isAuxiliary
            ? {}
            : {
              activityType,
              activityDescription,
            }),
        };

        await addDoc(collection(db, 'users', uid, colName), sessionData);

        // Save to permanent history for heatmap (only for main sessions)
        if (!isAuxiliary) {
          await addDoc(collection(db, 'users', uid, 'sessionHistory'), {
            ...sessionData,
            savedAt: serverTimestamp(), // Useful for debugging/sorting
            source: 'timer'
          });
        }
        if (!silent && !isAuxiliary) {
          // Use functional update to ensure we're using the latest state, 
          // but better yet, just increment by 1 based on current length.
          // However, since we just added a doc, the snapshot listener in FocusRecord 
          // will eventually update the list. But for the modal, we need an immediate number.
          // We'll trust sessions.length + 1.
          setModalSession(sessions.length + 1);
        }
        clearActiveTimerDoc();
      } catch (error) {
        console.warn('Failed to persist session', error);
        // If failed, allow retry? Maybe reset lastFinalizedStartRef?
        // For now, let's keep it simple.
      } finally {
        // Reset the ref after a delay to allow for state updates and prevent rapid double-clicks/events
        setTimeout(() => {
          isFinalizingRef.current = false;
        }, 2000);
      }
    },
    [uid, sessionStart, baseDuration, intentDetails, isAuxiliary, sessions, clearActiveTimerDoc]
  );

  useEffect(() => {
    if (!timerDocRef || !durationLoaded) {
      return undefined;
    }
    const unsubscribe = onSnapshot(
      timerDocRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          return;
        }
        const data = snapshot.data();
        const targetDate = data.targetTimestamp?.toDate
          ? data.targetTimestamp.toDate()
          : data.targetTimestamp
            ? new Date(data.targetTimestamp)
            : null;
        if (!targetDate) {
          return;
        }
        const startDate = data.startTimestamp?.toDate
          ? data.startTimestamp.toDate()
          : data.startTimestamp
            ? new Date(data.startTimestamp)
            : new Date();
        const remainingMs = targetDate.getTime() - Date.now();
        if (remainingMs <= 0) {
          if (isAuxiliary) {
            handleAuxiliaryFailure();
          } else {
            finalizeSession({ endDate: targetDate, silent: isAuxiliary, startOverride: startDate });
            clearActiveTimerDoc();
          }
          return;
        }
        if (!isActive) {
          setBaseDuration(data.baseDuration || baseDuration);
          setSessionStart(startDate);
          setTargetTimestamp(targetDate.getTime());
          setTimeLeft(Math.ceil(remainingMs / 1000));
          setIsActive(true);
          lastTickRef.current = null;
          setShowModal(false);
          setShowFocusFailPrompt(false);
          if (data.intentDetails) {
            setIntentDetails(data.intentDetails);
          }
        }
      },
      () => { }
    );
    return () => unsubscribe();
  }, [timerDocRef, isActive, baseDuration, clearActiveTimerDoc, finalizeSession, isAuxiliary, durationLoaded, handleAuxiliaryFailure]);

  useEffect(() => {
    if (!isActive || !targetTimestamp) {
      return undefined;
    }

    const tick = () => {
      const remainingMs = Math.max(0, targetTimestamp - Date.now());
      const nextSeconds = Math.ceil(remainingMs / 1000);
      setTimeLeft(nextSeconds);

      if (isAuxiliary && nextSeconds === 5 && remainingMs > 0 && lastTickRef.current !== 5) {
        auxSoundRef.current?.play().catch(() => { });
      }
      lastTickRef.current = nextSeconds;

      if (remainingMs <= 0) {
        setIsActive(false);
        setTargetTimestamp(null);

        if (isAuxiliary) {
          // Failed to start main task in time
          handleAuxiliaryFailure();
        } else {
          finalizeSession({ silent: isAuxiliary });
          clearActiveTimerDoc();
          mainSoundRef.current?.play().catch(() => { });
          setShowModal(true);
        }
        setSessionStart(null);
        setShowFocusFailPrompt(false);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isActive, targetTimestamp, finalizeSession, isAuxiliary, clearActiveTimerDoc, handleAuxiliaryFailure]);

  // Removed handleGraceExpired and its useEffect since grace period is now implicit in the last 5 seconds

  const progressPercent = useMemo(() => {
    if (baseDuration === 0) {
      return 0;
    }
    const elapsed = baseDuration - timeLeft;
    const percentage = (elapsed / baseDuration) * 100;
    return Math.min(Math.max(percentage, 8), 92);
  }, [baseDuration, timeLeft]);

  const beginSession = useCallback(() => {
    const startDate = new Date();
    setSessionStart(startDate);
    const targetMs = startDate.getTime() + baseDuration * 1000;
    setTargetTimestamp(targetMs);
    setIsActive(true);
    lastTickRef.current = null;
    startSoundRef.current?.play().catch(() => { });
    persistActiveTimer(startDate, new Date(targetMs), intentDetails);
  }, [baseDuration, persistActiveTimer, intentDetails]);

  const handleStart = () => {
    if (timeLeft > 0 && !isActive) {
      if (intentStorageKey) {
        openIntentModal();
        return;
      }
      beginSession();
    }
  };

  const handleModalRestart = () => {
    setTimeLeft(baseDuration);
    beginSession();
    setShowModal(false);
    setConfirmContinue(false);
  };

  const handleReturnToStudyRoom = () => {
    setShowModal(false);
    navigate('/');
    setConfirmContinue(false);
  };

  const handleContinueInitiate = () => {
    setConfirmContinue(true);
  };

  const handleContinueConfirm = () => {
    handleModalRestart();
  };

  useEffect(() => {
    if (isAuxiliary || typeof window === 'undefined' || !durationLoaded) {
      return;
    }
    const autoStart = localStorage.getItem(AUTO_START_MAIN_KEY);
    if (autoStart === '1' && !isActive && timeLeft > 0) {
      localStorage.removeItem(AUTO_START_MAIN_KEY);
      beginSession();
    }
  }, [isAuxiliary, isActive, timeLeft, beginSession, durationLoaded]);

  const handleImmediateStart = useCallback(() => {
    // Stop the warning sound immediately
    if (auxSoundRef.current) {
      auxSoundRef.current.pause();
      auxSoundRef.current.currentTime = 0;
    }

    if (isActive) {
      finalizeSession({ silent: true });
    }
    setIsActive(false);
    setTimeLeft(baseDuration);
    setSessionStart(null);
    setTargetTimestamp(null);
    setShowFocusFailPrompt(false);
    clearActiveTimerDoc();

    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(AUTO_START_MAIN_KEY, '1');
      } catch (error) {
        console.warn('Failed to schedule auto start', error);
      }
    }
    navigate('/timer');
  }, [isActive, finalizeSession, baseDuration, navigate, clearActiveTimerDoc]);

  const handleCancelReservation = () => {
    setShowCancelPrompt(true);
  };

  const handleCancelReservationConfirm = async () => {
    setShowCancelPrompt(false);
    setIsActive(false);
    setTimeLeft(baseDuration);
    setSessionStart(null);
    setTargetTimestamp(null);
    setShowModal(false);
    setShowFocusFailPrompt(false);
    clearActiveTimerDoc();
    await clearSessions();
  };

  const handleCancelReservationClose = () => {
    setShowCancelPrompt(false);
  };

  const handleFocusFailInitiate = () => {
    if (!storageKey) {
      return;
    }
    setShowFocusFailPrompt(true);
  };

  const handleFocusFailCancel = () => {
    setShowFocusFailPrompt(false);
  };

  const handleFocusFailConfirm = async () => {
    failureSoundRef.current?.play().catch(() => { });
    await saveToGraveyard();
    await clearSessions();
    setIsActive(false);
    setTimeLeft(baseDuration);
    setSessionStart(null);
    setTargetTimestamp(null);
    setShowModal(false);
    setShowFocusFailPrompt(false);
    clearActiveTimerDoc();
  };

  const requiresCustomCategory = selectedCategory === 'custom';

  const resolvedCategoryLabel = useMemo(() => {
    if (!requiresCustomCategory) {
      return (
        TASK_CATEGORY_OPTIONS.find((option) => option.value === selectedCategory)?.label ?? selectedCategory
      );
    }
    const trimmed = customCategory.trim();
    return trimmed.length > 0 ? trimmed : 'Custom';
  }, [selectedCategory, customCategory, requiresCustomCategory]);

  const logEntries = useMemo(() => {
    if (!sessions?.length) {
      return [];
    }
    return sessions;
  }, [sessions]);

  const trophyCount = useMemo(() => (isAuxiliary ? 0 : sessions.length), [isAuxiliary, sessions]);

  const openIntentModal = () => {
    if (intentDetails) {
      setSelectedCategory(intentDetails.categoryValue ?? DEFAULT_TASK_CATEGORY);
      if ((intentDetails.categoryValue ?? '') === 'custom') {
        setCustomCategory(intentDetails.categoryLabel ?? '');
      } else {
        setCustomCategory('');
      }
      setDescriptionInput(intentDetails.description ?? '');
    } else {
      setSelectedCategory(DEFAULT_TASK_CATEGORY);
      setCustomCategory('');
      setDescriptionInput('');
    }
    setIsIntentModalOpen(true);
  };

  const resetIntentModalState = () => {
    setIsIntentModalOpen(false);
  };

  const handleIntentModalStart = () => {
    if (requiresCustomCategory && customCategory.trim().length === 0) {
      return;
    }
    const intent = {
      categoryLabel: resolvedCategoryLabel,
      categoryValue: selectedCategory,
      description: descriptionInput.trim(),
      startTime: new Date().toISOString(),
    };

    setIntentDetails(intent);
    if (intentStorageKey && typeof window !== 'undefined') {
      try {
        localStorage.setItem(intentStorageKey, JSON.stringify(intent));
      } catch (error) {
        console.warn('Failed to persist session intent', error);
      }
    }
    setTimeLeft(baseDuration);
    beginSession();
    setIsIntentModalOpen(false);
  };


  const openDurationModal = () => {
    if (!isActive) {
      setShowDurationModal(true);
    }
  };

  const closeDurationModal = () => {
    setShowDurationModal(false);
    setDurationError('');
  };

  const handleDurationSubmit = (event) => {
    event.preventDefault();
    const hoursValue = Number(customHours);
    const minutesValue = Number(customMinutes);
    const secondsValue = Number(customSeconds);
    if (
      Number.isNaN(hoursValue) ||
      Number.isNaN(minutesValue) ||
      Number.isNaN(secondsValue) ||
      hoursValue < 0 ||
      minutesValue < 0 ||
      minutesValue >= 60 ||
      secondsValue < 0 ||
      secondsValue >= 60
    ) {
      setDurationError('Enter hours ≥ 0, minutes 0-59, and seconds 0-59.');
      return;
    }
    const totalDuration = hoursValue * 3600 + minutesValue * 60 + secondsValue;
    if (totalDuration <= 0) {
      setDurationError('Timer length must be greater than zero.');
      return;
    }

    const newDuration = Math.round(totalDuration);
    setBaseDuration(newDuration);
    setTimeLeft(newDuration);
    setIsActive(false);
    setShowModal(false);
    setSessionStart(null);
    setTargetTimestamp(null);
    setDurationError('');
    setShowDurationModal(false);
    clearActiveTimerDoc();

    if (typeof window !== 'undefined' && userDurationPreferenceKey) {
      try {
        localStorage.setItem(userDurationPreferenceKey, String(newDuration));
      } catch (error) {
        console.warn('Failed to persist custom duration', error);
      }
    }
  };

  const [isEditingIntent, setIsEditingIntent] = useState(false);
  const [editCategory, setEditCategory] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const handleEditIntent = () => {
    const currentVal = intentDetails?.categoryValue || DEFAULT_TASK_CATEGORY;
    setEditCategory(currentVal);
    if (currentVal === 'custom') {
      setCustomCategory(intentDetails?.categoryLabel || '');
    } else {
      setCustomCategory('');
    }
    setEditDescription(intentDetails?.description || '');
    setIsEditingIntent(true);
  };

  const handleSaveIntent = async () => {
    let finalLabel = '';
    if (editCategory === 'custom') {
      finalLabel = customCategory.trim() || 'Custom';
    } else {
      finalLabel = TASK_CATEGORY_OPTIONS.find(o => o.value === editCategory)?.label || editCategory;
    }

    const updatedIntent = {
      ...intentDetails,
      categoryLabel: finalLabel,
      categoryValue: editCategory,
      description: editDescription,
    };

    setIntentDetails(updatedIntent);
    setIsEditingIntent(false);

    // Update Local Storage
    if (intentStorageKey && typeof window !== 'undefined') {
      try {
        localStorage.setItem(intentStorageKey, JSON.stringify(updatedIntent));
      } catch (error) {
        console.warn('Failed to update session intent in storage', error);
      }
    }

    // Update Firestore
    if (timerDocRef) {
      try {
        await setDoc(timerDocRef, { intentDetails: updatedIntent }, { merge: true });
      } catch (error) {
        console.warn('Failed to update session intent in firestore', error);
      }
    }
  };

  const activeTitle = isActive && intentDetails?.categoryLabel ? intentDetails.categoryLabel : null;
  const activeSubtitle = isActive && intentDetails?.description ? intentDetails.description : null;

  useEffect(() => {
    if (typeof window !== 'undefined' && isActive && !isAuxiliary) {
      document.body.classList.add('timer-active');
    } else {
      document.body.classList.remove('timer-active');
    }
    return () => {
      document.body.classList.remove('timer-active');
    };
  }, [isActive, isAuxiliary]);

  return (
    <div className={`timer-page${isActive ? ' timer-page--active' : ''}`}>
      {isActive && isEditingIntent ? (
        <div className="timer-page__edit-intent">
          <select
            className="timer-page__edit-input timer-page__edit-input--title"
            value={editCategory}
            onChange={(e) => setEditCategory(e.target.value)}
            style={{ appearance: 'none', cursor: 'pointer' }}
          >
            {TASK_CATEGORY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {editCategory === 'custom' && (
            <input
              type="text"
              className="timer-page__edit-input timer-page__edit-input--title"
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              placeholder="Custom Category Name"
              style={{ marginTop: '0.5rem' }}
              autoFocus
            />
          )}

          <input
            type="text"
            className="timer-page__edit-input timer-page__edit-input--subtitle"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Task Description"
          />
          <div className="timer-page__edit-actions">
            <button onClick={handleSaveIntent} className="timer-page__edit-save">Save</button>
            <button onClick={() => setIsEditingIntent(false)} className="timer-page__edit-cancel">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="timer-page__header-group">
            <h1 className={`timer-page__title${activeTitle ? ' timer-page__title--active' : ''}`}>
              {activeTitle || title}
            </h1>
            {isActive && (
              <button
                className="timer-page__edit-btn"
                onClick={handleEditIntent}
                title="Edit Task"
              >
                ✎
              </button>
            )}
          </div>
          {activeSubtitle && <p className="timer-page__subtitle">{activeSubtitle}</p>}
        </>
      )}
      <div className="timer-display-wrapper">
        {!isAuxiliary && <div className="timer-display">{formatTime(timeLeft)}</div>}
        {!isActive && !isAuxiliary && (
          <button
            type="button"
            className="timer-display__action"
            onClick={openDurationModal}
          >
            Adjust Timer
          </button>
        )}
      </div>

      {isAuxiliary ? (
        <>
          <CircularTimer
            timeLeft={timeLeft}
            duration={baseDuration}
            title={activeTitle || title}
            isWarning={timeLeft <= 5}
          />
          {!isActive && (
            <button
              type="button"
              className="timer-display__action"
              onClick={openDurationModal}
              style={{ marginTop: '1rem' }}
            >
              Adjust Timer
            </button>
          )}
        </>
      ) : (
        <div className="timer-progress">
          <div className="timer-progress__track">
            <span
              className="timer-progress__figure"
              style={{ left: `${progressPercent}%` }}
              role="img"
              aria-label="Focus traveler"
            >
              <WalkerIcon />
            </span>
            <span className="timer-progress__flag" role="img" aria-label="Goal flag">
              <FlagIcon />
            </span>
            <div className="timer-progress__line" />
          </div>
        </div>
      )}
      {!isActive && (
        <div className="controls controls--timer controls--primary">
          <button type="button" onClick={handleStart} disabled={timeLeft === 0}>
            Start
          </button>
          <NavLink to="/focus-record" className="controls__link-button">
            Focus Record
          </NavLink>
        </div>
      )}
      {isIntentModalOpen && (
        <div
          className="sacred-modal__overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="intent-modal-title"
        >
          <div className="sacred-modal">
            <h2 id="intent-modal-title" className="sacred-modal__title">
              What are you going to focus on?
            </h2>
            <label className="sacred-modal__label">
              Category
              <select
                className="sacred-modal__select"
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
              >
                {TASK_CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {requiresCustomCategory && (
              <label className="sacred-modal__label">
                Custom category
                <input
                  type="text"
                  className="sacred-modal__input"
                  placeholder="e.g. Deep Work Sprint"
                  value={customCategory}
                  onChange={(event) => setCustomCategory(event.target.value)}
                />
              </label>
            )}
            <label className="sacred-modal__label">
              Description
              <textarea
                className="sacred-modal__textarea"
                placeholder="Describe the focus task"
                value={descriptionInput}
                onChange={(event) => setDescriptionInput(event.target.value)}
                rows={4}
              />
            </label>
            <div className="sacred-modal__actions">
              <p className="sacred-modal__warning">
                {isAuxiliary
                  ? `Once you start this timer, you must begin the main task within ${auxiliaryWindowLabel}. If you don't, all records will be erased.`
                  : 'Once you press Start Session, Sacred Seat protocol is in effect—stay fully focused for the entire timer or your record will be reset.'}
              </p>
              <button
                type="button"
                className="sacred-modal__secondary"
                onClick={resetIntentModalState}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sacred-modal__primary"
                onClick={handleIntentModalStart}
                disabled={requiresCustomCategory && customCategory.trim().length === 0}
              >
                Start Session
              </button>
            </div>
          </div>
        </div>
      )}
      {storageKey && isAuxiliary && (
        <div className="timer-log">
          <h2 className="timer-log__title">Reservation Record</h2>
          {logEntries.length === 0 ? (
            <p className="timer-log__empty">No reservations recorded yet.</p>
          ) : (
            <div className="timer-log__entries">
              {logEntries.map((session) => (
                <div className="timer-log__entry" key={`${session.number}-${session.date}-${session.startTime}`}>
                  <div className="timer-log__meta">
                    <span className="timer-log__activity">Successful Reservation #{session.chainNumber || session.number}</span>
                  </div>
                  <div className="timer-log__details timer-log__details--full">
                    <div className="timer-log__time">
                      {session.date} {session.startTime} - {session.endTime}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!isAuxiliary && (
        <div className="timer-trophies">
          <h2 className="timer-trophies__title">Trophy Cabinet</h2>
          {trophyCount === 0 ? (
            <p className="timer-trophies__empty">Complete a focus session to earn your first trophy.</p>
          ) : (
            <div className="timer-trophies__grid">
              {Array.from({ length: trophyCount }).map((_, index) => (
                <div className="timer-trophies__item" key={`trophy-${index + 1}`}>
                  <TrophyIcon />
                  <span>#{index + 1}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {showDurationModal && (
        <div
          className="sacred-modal__overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="timer-settings-title"
        >
          <div className="sacred-modal">
            <h2 id="timer-settings-title" className="sacred-modal__title">
              Customize Timer Length
            </h2>
            <form className="timer-settings" onSubmit={handleDurationSubmit}>
              <div className="timer-settings__inputs">
                <label className="timer-settings__label">
                  Hours
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="timer-settings__input"
                    value={customHours}
                    onChange={(event) => {
                      setCustomHours(event.target.value);
                      setDurationError('');
                    }}
                  />
                </label>
                <label className="timer-settings__label">
                  Minutes
                  <input
                    type="number"
                    min="0"
                    max="59"
                    step="1"
                    className="timer-settings__input"
                    value={customMinutes}
                    onChange={(event) => {
                      setCustomMinutes(event.target.value);
                      setDurationError('');
                    }}
                  />
                </label>
                <label className="timer-settings__label">
                  Seconds
                  <input
                    type="number"
                    min="0"
                    max="59"
                    step="1"
                    className="timer-settings__input"
                    value={customSeconds}
                    onChange={(event) => {
                      setCustomSeconds(event.target.value);
                      setDurationError('');
                    }}
                  />
                </label>
              </div>
              <p className="timer-settings__hint">
                Currently set to {formatTime(baseDuration)}
              </p>
              {durationError && <p className="timer-settings__error">{durationError}</p>}
              <div className="timer-settings__actions">
                <button type="button" className="timer-settings__cancel" onClick={closeDurationModal}>
                  Cancel
                </button>
                <button type="submit" className="timer-settings__apply">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isActive && (
        <div className="controls controls--timer controls--secondary">
          {isAuxiliary ? (
            <>
              <button
                type="button"
                className="controls__start-task"
                onClick={handleImmediateStart}
              >
                Start Task Now
              </button>
              <button
                type="button"
                className="controls__cancel"
                onClick={handleCancelReservation}
              >
                Cancel Reservation
              </button>
            </>
          ) : (
            storageKey && (
              <button
                type="button"
                className="controls__fail"
                onClick={handleFocusFailInitiate}
              >
                I lost focus
              </button>
            )
          )}
        </div>
      )}

      {showModal && (
        <div className="timer-modal__overlay" role="dialog" aria-modal="true">
          <div className="timer-modal">
            <h2 className="timer-modal__title">
              🎉 Congratulations! {successPrefix} #{modalSession} Complete!
            </h2>
            <p className="timer-modal__subtitle">
              You crushed that focus block. Ready to keep the momentum going?
            </p>
            <div className="timer-modal__actions">
              {!confirmContinue ? (
                <>
                  <button
                    type="button"
                    className="timer-modal__primary"
                    onClick={handleContinueInitiate}
                  >
                    Continue Same Task
                  </button>
                  <button
                    type="button"
                    className="timer-modal__secondary"
                    onClick={handleReturnToStudyRoom}
                  >
                    Return to Study Room
                  </button>
                </>
              ) : (
                <>
                  <div className="timer-modal__warning">
                    <strong>Warning:</strong> Sacred Seat protocol will restart. If you lose focus this streak will reset.
                  </div>
                  <button
                    type="button"
                    className="timer-modal__primary timer-modal__primary--success"
                    onClick={handleContinueConfirm}
                  >
                    Yes, continue this task
                  </button>
                  <button
                    type="button"
                    className="timer-modal__secondary"
                    onClick={handleReturnToStudyRoom}
                  >
                    Return to Study Room
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {isAuxiliary && isActive && timeLeft <= 5 && (
        <div className="timer-modal__overlay" role="dialog" aria-modal="true">
          <div className="timer-modal timer-modal--warning">
            <h2 className="timer-modal__title">Time to LOCK IN</h2>
            <p className="timer-modal__subtitle">
              Start your main task now. Records reset in {timeLeft}s if you don't continue.
            </p>
            <div className="timer-modal__actions">
              <button
                type="button"
                className="timer-modal__primary"
                onClick={handleImmediateStart}
              >
                Start Task Now
              </button>
            </div>
          </div>
        </div>
      )}
      {showCancelPrompt && (
        <div className="timer-modal__overlay" role="dialog" aria-modal="true">
          <div className="timer-modal timer-modal--warning">
            <h2 className="timer-modal__title">Cancel Reservation?</h2>
            <p className="timer-modal__subtitle">
              Are you sure you want to cancel this reservation? All records will be erased if you do so.
            </p>
            <div className="timer-modal__actions">
              <button
                type="button"
                className="timer-modal__secondary"
                onClick={handleCancelReservationClose}
              >
                Keep Reservation
              </button>
              <button
                type="button"
                className="timer-modal__danger"
                onClick={handleCancelReservationConfirm}
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showFocusFailPrompt && (
        <div className="timer-modal__overlay" role="dialog" aria-modal="true">
          <div className="timer-modal timer-modal--warning">
            <h2 className="timer-modal__title">⚠️ Reset Streak?</h2>
            <p className="timer-modal__subtitle">
              This will erase every recorded session and restart your streak. Only proceed if this block
              truly failed.
            </p>
            <div className="timer-modal__actions">
              <button
                type="button"
                className="timer-modal__secondary"
                onClick={handleFocusFailCancel}
              >
                Keep Streak
              </button>
              <button
                type="button"
                className="timer-modal__danger"
                onClick={handleFocusFailConfirm}
              >
                Yes, reset everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimerPage;
